import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import WhatsAppFloat from "@/components/WhatsAppFloat";
import SEO, { COMPANY } from "@/components/SEO";
import { useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { Mail, Phone, MapPin, Train, Mountain, Anchor, Compass, MessageCircle, ShieldCheck, Plane, Sparkles } from "lucide-react";
import { trackLead } from "@/lib/analytics";

// 4 specialty pillars — surfaced in the About hero, schema, and homepage 'Why Choose Us'.
const SPECIALTIES = [
  {
    icon: Train,
    title: "Swiss Rail & European Holidays",
    body:
      "Swiss Rail Pass bookings, luxury Swiss Alps family tours, scenic Paris-to-Zurich train packages and bespoke European holiday planning for travellers across India and abroad.",
    keywords: "Swiss Rail Pass · Luxury Swiss Tours · Europe Holiday Planner",
  },
  {
    icon: Mountain,
    title: "Leh, Andaman & Kerala Specialists",
    body:
      "Leh Ladakh luxury 4×4 tours, Andaman honeymoon packages, Kerala houseboat & backwater specials, and Spiti Valley guided expeditions — handled by curators who've personally driven every route.",
    keywords: "Leh Ladakh Specialists · Andaman Honeymoon · Kerala Backwater Specialists",
  },
  {
    icon: Compass,
    title: "Spiritual & Cruise Experts",
    body:
      "Chardham Yatra by helicopter, Varanasi & Ayodhya spiritual circuits, Singapore-Malaysia cruise deals for families and luxury river-cruise bookings across India and Europe.",
    keywords: "Chardham Yatra Helicopter Booking · Luxury Cruises · Spiritual Circuits",
  },
  {
    icon: Anchor,
    title: "Luxury Global Holidays",
    body:
      "Japan Cherry Blossom tours 2026, Maldives water-villa deals, USA East Coast family vacation packages and bespoke Maldives luxury honeymoon experiences — booked at offline negotiated rates.",
    keywords: "Maldives Luxury Honeymoon Agent · Japan Cherry Blossom 2026 · USA Family Packages",
  },
];

const WHY_US = [
  {
    icon: ShieldCheck,
    title: "Founded in 2014 · 4.8★ on Google",
    body:
      "Rated 4.8 on Google by 264+ travellers. A customized holiday studio with its office in Ludhiana, serving families and honeymooners across India and abroad.",
  },
  {
    icon: Plane,
    title: "Direct flights & negotiated rates",
    body:
      "We prioritise direct departures from your nearest airport and offline-negotiated hotel rates 10–15% lower than top OTAs.",
  },
  {
    icon: Sparkles,
    title: "Hand-curated, never templated",
    body:
      "Every itinerary is written by a senior curator — not a script. Custom PDF itineraries with three hotel tiers (Boutique · Luxury · Value) for every quote.",
  },
];

export default function About() {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 100);
    } else {
      window.scrollTo(0, 0);
    }
  }, [hash]);

  return (
    <div className="min-h-screen bg-[var(--vp-bg)]" data-testid="about-page">
      <SEO
        title="About Vacations Pro | Customized Honeymoon & Family Holiday Planner"
        description="Vacations Pro International Private Limited — founded in 2014, office in Ludhiana, serving travellers across India and abroad. Swiss Rail & Europe, Leh Ladakh, Andaman, Kerala, Chardham by helicopter and Maldives honeymoons. 4.8★ on Google · 264+ reviews."
        path="/about"
      />
      <Navbar variant="solid" />

      {/* Hero */}
      <section className="bg-white px-6 md:px-12 pt-16 md:pt-24 pb-16 border-b border-[var(--vp-border)]">
        <div className="max-w-4xl mx-auto text-center">
          <span className="vp-label block mb-4" data-testid="about-founded-label">FOUNDED IN 2014 · OFFICE IN LUDHIANA · INDIA & ABROAD</span>
          <h1 className="font-serif-vp text-4xl md:text-6xl text-[var(--vp-primary)] leading-[1.05] mb-6">
            Customized holidays, planned by people.<br />
            <em className="italic text-[var(--vp-secondary)]">For honeymooners and families across India and abroad.</em>
          </h1>
          <p className="font-sans-vp text-[var(--vp-muted)] max-w-2xl mx-auto leading-relaxed">
            {COMPANY.legalName} is a premium travel studio specialising in Swiss Rail Pass
            bookings, luxury Swiss Alps family tours, Leh Ladakh 4×4 expeditions, Andaman & Kerala
            holidays, Chardham Yatra by helicopter, and Maldives luxury honeymoons — all crafted
            by curators who've personally walked the routes. Founded in 2014, serving travellers nationwide
            from our office in Ludhiana.
          </p>
          <div className="mt-10 flex items-center justify-center gap-6 flex-wrap text-sm text-[var(--vp-muted)]" data-testid="about-contact-line">
            <div className="flex items-center gap-2"><MapPin className="w-4 h-4" /> {COMPANY.addressInline}</div>
            {COMPANY.phones.map((p) => (
              <a key={p.tel} href={`tel:${p.tel}`} className="flex items-center gap-2 hover:text-[var(--vp-primary)]"><Phone className="w-4 h-4" /> {p.display}</a>
            ))}
            <div className="flex items-center gap-2"><Mail className="w-4 h-4" /> {COMPANY.email}</div>
          </div>
          <div className="mt-8">
            <a
              href="https://wa.me/919501030117?text=Hi%20Vacations%20Pro%20%E2%80%94%20I%27d%20like%20to%20plan%20a%20trip."
              target="_blank" rel="noopener noreferrer"
              onClick={() => trackLead({ source: "about_hero_whatsapp" })}
              className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe5d] text-white rounded-full px-7 py-3 text-sm tracking-[0.1em] uppercase font-bold transition-colors"
              data-testid="about-whatsapp-cta"
            >
              <MessageCircle className="w-4 h-4" /> Enquire Now on WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* Specialties — 4 pillars (replaces vague "About") */}
      <section id="specialties" className="px-6 md:px-12 py-20 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-baseline gap-3 mb-6">
            <Sparkles className="w-5 h-5 text-[var(--vp-secondary)]" />
            <span className="vp-label">WHAT WE SPECIALISE IN</span>
          </div>
          <h2 className="font-serif-vp text-3xl md:text-4xl text-[var(--vp-primary)] mb-12 max-w-3xl">
            Four pillars. One studio. Every itinerary planned by hand.
          </h2>
          <div className="grid md:grid-cols-2 gap-6" data-testid="about-specialties">
            {SPECIALTIES.map(({ icon: Icon, title, body, keywords }) => (
              <div key={title} className="vp-card p-6 md:p-7">
                <div className="w-12 h-12 rounded-xl bg-[var(--vp-primary)]/10 flex items-center justify-center mb-5">
                  <Icon className="w-6 h-6 text-[var(--vp-primary)]" strokeWidth={1.6} />
                </div>
                <h3 className="font-serif-vp text-2xl text-[var(--vp-primary)] leading-tight mb-3">{title}</h3>
                <p className="text-sm text-[var(--vp-muted)] leading-relaxed mb-4">{body}</p>
                <div className="text-[10px] tracking-[0.18em] uppercase text-[var(--vp-secondary)] font-semibold">
                  {keywords}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section id="why-us" className="px-6 md:px-12 py-20 bg-[var(--vp-bg)] border-y border-[var(--vp-border)]">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-baseline gap-3 mb-6">
            <ShieldCheck className="w-5 h-5 text-[var(--vp-secondary)]" />
            <span className="vp-label">WHY CHOOSE US</span>
          </div>
          <h2 className="font-serif-vp text-3xl md:text-4xl text-[var(--vp-primary)] mb-12 max-w-3xl">
            Why travellers across India and abroad choose us.
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {WHY_US.map(({ icon: Icon, title, body }) => (
              <div key={title} className="bg-white border border-[var(--vp-border)] rounded-xl p-6">
                <div className="w-10 h-10 rounded-full bg-[var(--vp-secondary)]/10 flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-[var(--vp-secondary)]" strokeWidth={1.6} />
                </div>
                <h3 className="font-serif-vp text-xl text-[var(--vp-primary)] mb-2">{title}</h3>
                <p className="text-sm text-[var(--vp-muted)] leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
          <div className="mt-12 text-center">
            <Link
              to="/"
              className="inline-flex items-center gap-2 vp-btn-primary text-xs py-3.5 px-8"
              data-testid="about-plan-trip-cta"
            >
              <Sparkles className="w-4 h-4" />
              Plan My Trip with the AI Concierge
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
